import { useState } from 'react';
import { Box, Button, Chip, FormHelperText, Typography } from '@coolblue-development/freddy';
import { Plus } from '@coolblue-development/icons';
import { EMAIL_REGEX } from '../../../../../utils/regex';

const ROW_HEIGHT = '28px';
const CHIP_STYLE = { height: '24px', padding: '0 8px', fontSize: '12px' };
const INPUT_STYLE = {
  flex: 1,
  minWidth: '160px',
  height: ROW_HEIGHT,
  padding: 0,
  border: 'none',
  outline: 'none',
  background: 'none',
  font: 'inherit',
  fontSize: '12px'
};

interface RecipientFieldProps {
  label: string;
  addLabel: string;
  recipients: string[];
  onChange: (recipients: string[]) => void;
  maxRecipients: number;
  maxRecipientsMessage?: string;
  /** Addresses already used elsewhere on the email, e.g. in a sibling recipient field. */
  takenAddresses?: string[];
  disabled?: boolean;
}

const RecipientField = ({
  label,
  addLabel,
  recipients,
  onChange,
  maxRecipients,
  maxRecipientsMessage,
  takenAddresses = [],
  disabled = false
}: RecipientFieldProps): JSX.Element => {
  const [isAdding, setIsAdding] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string>();

  const getAddressError = (address: string): string | undefined => {
    if (!EMAIL_REGEX.test(address)) return 'Enter a valid email address';
    if ([...recipients, ...takenAddresses].some(taken => taken.toLowerCase() === address.toLowerCase())) {
      return 'This address is already added';
    }
    return undefined;
  };

  const addTypedRecipient = (): void => {
    const address = inputValue.trim();
    if (!address) {
      setIsAdding(false);
      return;
    }

    const addressError = getAddressError(address);
    if (addressError) {
      setError(addressError);
      return;
    }

    onChange([...recipients, address]);
    setInputValue('');
    setIsAdding(false);
  };

  if (recipients.length === 0 && !isAdding) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: ROW_HEIGHT }}>
        <Button
          variant="tertiary"
          size="small"
          startIcon={<Plus size="small" />}
          disabled={disabled}
          onClick={() => setIsAdding(true)}
          sx={theme => ({
            '--cb-button-min-height': ROW_HEIGHT,
            '--cb-button-padding': '0 4px',
            '--cb-button-color-text': theme.color.font.base,
            fontWeight: theme.typography.fontWeight.openSans.normal,
            '& svg': { color: theme.color.icon.interactiveSubtle }
          })}
        >
          {addLabel}
        </Button>
      </Box>
    );
  }

  const canAddMore = recipients.length < maxRecipients;
  const helperText = error ?? (recipients.length > maxRecipients ? maxRecipientsMessage : undefined);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
        <Typography
          variant="caption"
          component="span"
          color="subtle"
          sx={{ lineHeight: ROW_HEIGHT, whiteSpace: 'nowrap' }}
        >
          {`${label}:`}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
          {recipients.map(recipient => (
            <Chip
              key={recipient}
              size="small"
              variant="button-dismissible"
              disabled={disabled}
              sx={CHIP_STYLE}
              aria-label={`Remove ${recipient}`}
              onClick={() => onChange(recipients.filter(r => r !== recipient))}
              onKeyDown={event => {
                if (event.key === 'Backspace' || event.key === 'Delete') {
                  event.preventDefault();
                  onChange(recipients.filter(r => r !== recipient));
                }
              }}
            >
              {recipient}
            </Chip>
          ))}
          {canAddMore && (
            <input
              style={INPUT_STYLE}
              aria-label={label}
              placeholder="Add address"
              disabled={disabled}
              autoFocus={isAdding}
              value={inputValue}
              onChange={event => {
                setInputValue(event.target.value);
                setError(undefined);
              }}
              onKeyDown={event => {
                if (event.key === 'Backspace' && inputValue === '' && recipients.length > 0) {
                  onChange(recipients.slice(0, -1));
                }
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addTypedRecipient();
                }
              }}
              onBlur={addTypedRecipient}
              aria-invalid={Boolean(error)}
            />
          )}
        </Box>
      </Box>
      {helperText && <FormHelperText validation="error">{helperText}</FormHelperText>}
    </Box>
  );
};

export default RecipientField;
